/**
 * Знаки рубежей (ТЗ UI-70).
 *
 * Восемь знаков на восемь рубежей, по одному на смысл: объём, слова, точность,
 * регулярность, скорость, знаки, два языка, контрольный тест. Все нарисованы
 * для Type2X одной линией — токенов цвета в них нет, только `currentColor`,
 * поэтому тема перекрашивает их без замены файла.
 *
 * Знак никогда не стоит один: рядом с ним всегда название рубежа словами. Знак
 * — подсказка глазу, а не носитель смысла (ТЗ UI-70, UX-12).
 *
 * Полученное от неполученного знак не отличает: это делают галочка, слово и
 * число рядом. Одна смена цвета состоянием быть не может.
 */

import type { AchievementId } from '@type2x/feature-personal-workspace';

/** Линии каждого знака: путь в поле 24×24. */
const SHAPES: Record<AchievementId, React.ReactNode> = {
  // Объём набранного: столбики растут.
  'first-thousand': (
    <>
      <path d="M5 19 v-5" />
      <path d="M12 19 v-9" />
      <path d="M19 19 v-13" />
    </>
  ),
  // Контрольный тест: щит с отметкой.
  'first-verified': (
    <>
      <path d="M12 4 l7 3 v5 c0 4 -3 6.5 -7 8 -4 -1.5 -7 -4 -7 -8 V7 z" />
      <path d="M9 12 l2.5 2.5 4.5 -5" />
    </>
  ),
  // Точность: попадание в центр.
  'accuracy-95': (
    <>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  // Слова: три блока слова подряд.
  'ten-words': (
    <>
      <rect x="3" y="9" width="5" height="6" rx="1.5" />
      <rect x="10" y="9" width="5" height="6" rx="1.5" />
      <rect x="17" y="9" width="4" height="6" rx="1.5" />
    </>
  ),
  // Регулярность: неделя отметок подряд.
  'seven-days': (
    <>
      <path d="M4 8 h16" />
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M8 5 v3" />
      <path d="M16 5 v3" />
      <path d="M8 14 h1.5" />
      <path d="M12 14 h1.5" />
      <path d="M16 14 h1.5" />
    </>
  ),
  // Скорость: шкала со стрелкой.
  'verified-35-wpm': (
    <>
      <path d="M4 18 a8 8 0 0 1 16 0" />
      <path d="M12 18 l5 -6" />
    </>
  ),
  // Знаки: пара скобок вокруг точки.
  'symbols-confident': (
    <>
      <path d="M8 6 c-3 2 -3 10 0 12" />
      <path d="M16 6 c3 2 3 10 0 12" />
      <circle cx="12" cy="12" r="1.2" />
    </>
  ),
  // Два языка: две области, у которых есть общая часть.
  'two-languages': (
    <>
      <rect x="3" y="7" width="12" height="10" rx="2.5" />
      <rect x="9" y="7" width="12" height="10" rx="2.5" />
    </>
  ),
};

export interface AchievementIconProps {
  readonly id: AchievementId;
}

export function AchievementIcon({ id }: AchievementIconProps): React.JSX.Element {
  return (
    <svg
      className="t2x-achievement__icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      {SHAPES[id]}
    </svg>
  );
}
